import * as Highcharts from 'highcharts';
import { Component, OnDestroy, OnInit } from '@angular/core';

/**
 * Panel del login: el monitor de precios de la zona, en vivo.
 *
 * La gráfica es una gráfica del portal, no una ilustración: mismo motor
 * (Highcharts) y el mismo tratamiento que Evolución — rejilla punteada
 * `#dee2e6`, etiquetas de 10px en `#8c8c8c`, sin línea de eje ni leyenda
 * interna, marcador solo en el último punto, y la referencia como línea
 * PUNTEADA del mismo color al 35%, igual que su modo «Grupo vs Municipio».
 * Nada de áreas ni bandas: el portal no las usa en ninguna vista.
 *
 * Debajo, el tablero: cuando un competidor mueve su precio, las filas se
 * reordenan y tu posición cambia. Ese es el bucle de GAS PRE.
 *
 * Todas las cifras son sintéticas: esta pantalla corre antes de autenticarse y
 * nunca debe leer de la red.
 */

type ProductKey = 'regular' | 'premium' | 'diesel';

interface Product {
  key: ProductKey;
  label: string;
  color: string;
  /** Tu precio y el promedio de la zona, lectura por lectura. */
  own: number[];
  zone: number[];
}

interface Row {
  id: number;
  name: string;
  own: boolean;
  prices: Record<ProductKey, number>;
  fresh: boolean;
}

// ── Tratamiento de ejes (Evolución), en su versión para fondo oscuro ─────────
// Mismos elementos que en el portal —rejilla punteada, etiquetas de 10px, sin
// línea de eje— pero con el contraste corregido: sobre el azul del panel, el
// `#dee2e6` de la rejilla brilla más que las series y el `#8c8c8c` de las
// etiquetas se apaga.
const AXIS_GRID_COLOR = 'rgba(255, 255, 255, .11)';
const AXIS_LABEL_COLOR = 'rgba(255, 255, 255, .45)';
const TOOLTIP_BG = '#0d1a2b';
const Y_AXIS_CUTS = 5;
const LAST_POINT_MARKER_RADIUS = 3.5;
/** Holgura del eje Y sobre el rango de las series, como en Evolución. */
const Y_PADDING = 0.12;

/**
 * Productos y colores del portal: los mismos `PRODUCT_COLORS` y `PRODUCT_LABELS`
 * de Evolución. «Regular», no «Magna»: es el nombre que usa el portal.
 */
// Los tres productos del portal con sus colores de Evolución (`PRODUCT_COLORS`)
// llevados a fondo oscuro: sobre el azul del panel, el verde `#179446` y el rojo
// `#CD2120` se apagan y el tinta `#1e2228` desaparece. Mismo significado, con el
// contraste corregido — el verde es el `#36de87` del logo PRE, que es el que la
// marca usa justamente para fondos oscuros.
const PRODUCTS: { key: ProductKey; label: string; color: string; base: number }[] = [
  { key: 'regular', label: 'Regular', color: '#36de87', base: 23.79 },
  { key: 'premium', label: 'Premium', color: '#ff6b6b', base: 25.62 },
  { key: 'diesel',  label: 'Diesel',  color: '#93a4b8', base: 25.91 }
];

/** Ventana visible de la gráfica, en lecturas. Ocho lecturas ≈ una hora. */
const VISIBLE = 34;
/**
 * Rótulos del eje X por distancia al extremo derecho, no por posición absoluta:
 * `addPoint(shift)` hace avanzar la X, así que un eje clavado en 0–33 dejaría la
 * serie fuera de cuadro. La ventana sigue al dato y las marcas salen de su máximo.
 */
const X_LABELS: Record<number, string> = { 32: '−4 h', 24: '−3 h', 16: '−2 h', 8: '−1 h', 0: 'Ahora' };
const X_TICK_OFFSETS = [32, 24, 16, 8, 0];

// ── Ritmo ─────────────────────────────────────────────────────────────────────
const TICK_MS = 2000;
/** Un cambio de precio en el tablero cada dos lecturas y un producto nuevo cada
 *  seis: tres cadencias distintas, o el panel se lee como ruido. */
const PRICE_EVERY = 2;
const PRODUCT_EVERY = 6;
const FRESH_MS = 1200;

const ROW_HEIGHT = 38;
const COMPETITOR_NAMES = ['Reforma', 'Insurgentes', 'La Villa', 'Del Valle'];

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function money(value: number): string {
  return `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

@Component({
  selector: 'app-login-market',
  templateUrl: './login-market.component.html',
  styleUrls: ['./login-market.component.scss']
})
export class LoginMarketComponent implements OnInit, OnDestroy {
  private ticker: ReturnType<typeof setInterval> | null = null;
  private timeouts: ReturnType<typeof setTimeout>[] = [];
  private chart?: Highcharts.Chart;
  private ticks = 0;

  readonly rowHeight = ROW_HEIGHT;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options;

  products: Product[] = PRODUCTS.map(seed => ({
    key: seed.key,
    label: seed.label,
    color: seed.color,
    ...this.seedSeries(seed.base)
  }));

  active: ProductKey = 'regular';

  rows: Row[] = [
    { id: 0, name: 'Tu estación', own: true, prices: this.seedPrices(0), fresh: false },
    ...COMPETITOR_NAMES.map((name, i) => ({
      id: i + 1,
      name,
      own: false,
      prices: this.seedPrices(randomBetween(-0.42, 0.58)),
      fresh: false
    }))
  ];

  constructor() {
    this.chartOptions = this.buildOptions();
  }

  // ── Producto activo ────────────────────────────────────────────────────────

  get product(): Product {
    return this.products.find(p => p.key === this.active) as Product;
  }

  /** Tu precio vigente: la última lectura de la serie del producto activo. */
  get ownValue(): string {
    return money(this.product.own[this.product.own.length - 1]);
  }

  get zoneValue(): string {
    return money(this.product.zone[this.product.zone.length - 1]);
  }

  /** Variación de tu precio contra el inicio de la ventana, en centavos. */
  get delta(): number {
    const series = this.product.own;
    return Math.round((series[series.length - 1] - series[0]) * 100);
  }

  get deltaLabel(): string {
    if (this.delta === 0) { return 'sin cambio'; }
    return `${Math.abs(this.delta)}¢ en 4 h`;
  }

  // ── Tablero ────────────────────────────────────────────────────────────────

  get boardHeight(): number {
    return this.rows.length * ROW_HEIGHT;
  }

  get average(): number {
    return this.rows.reduce((sum, r) => sum + r.prices[this.active], 0) / this.rows.length;
  }

  /** Orden por el precio del producto activo: cambiar de producto reordena. */
  private get ranked(): number[] {
    return [...this.rows]
      .sort((a, b) => a.prices[this.active] - b.prices[this.active])
      .map(r => r.id);
  }

  rank(row: Row): number {
    return this.ranked.indexOf(row.id);
  }

  ownRankLabel(): string {
    const own = this.rows.find(r => r.own);
    return own ? `${this.rank(own) + 1}° de ${this.rows.length}` : '';
  }

  price(row: Row): string {
    return row.prices[this.active].toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  averageLabel(): string {
    return money(this.average);
  }

  /** Desvío contra el promedio de la zona, en centavos. */
  gap(row: Row): number {
    return Math.round((row.prices[this.active] - this.average) * 100);
  }

  gapLabel(row: Row): string {
    const cents = this.gap(row);
    if (cents === 0) { return '0¢'; }
    return `${cents > 0 ? '+' : '−'}${Math.abs(cents)}¢`;
  }

  /** Ancho de la barra sobre su mitad de la pista; satura a los 60 centavos. */
  gapWidth(row: Row): number {
    return Math.min(100, (Math.abs(this.gap(row)) / 60) * 100);
  }

  trackByRow(index: number, row: Row): number {
    return row.id;
  }

  trackByProduct(index: number, product: Product): string {
    return product.key;
  }

  // ── Ciclo de vida ──────────────────────────────────────────────────────────

  onChartInstance = (chart: Highcharts.Chart): void => {
    this.chart = chart;
  };

  ngOnInit(): void {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }
    this.ticker = setInterval(() => this.tick(), TICK_MS);
  }

  ngOnDestroy(): void {
    if (this.ticker) { clearInterval(this.ticker); }
    this.timeouts.forEach(clearTimeout);
  }

  // ── Interno ────────────────────────────────────────────────────────────────

  private seedSeries(base: number): { own: number[]; zone: number[] } {
    const own: number[] = [];
    const zone: number[] = [];
    let price = base - 0.14;
    let reference = base - 0.06;
    for (let i = 0; i < VISIBLE; i++) {
      price += randomBetween(-0.035, 0.042);
      reference += randomBetween(-0.03, 0.034);
      own.push(price);
      zone.push(reference);
    }
    return { own, zone };
  }

  private seedPrices(offset: number): Record<ProductKey, number> {
    return PRODUCTS.reduce((acc, p) => {
      acc[p.key] = p.base + offset + randomBetween(-0.08, 0.08);
      return acc;
    }, {} as Record<ProductKey, number>);
  }

  /**
   * Una lectura nueva por tick, en todos los productos: al cambiar de pestaña la
   * historia del nuevo ya está al día. `addPoint(..., shift: true)` es el modo en
   * vivo de Highcharts, que agrega a la derecha y saca el más viejo animando el
   * desplazamiento, sin redibujar la gráfica entera.
   */
  private tick(): void {
    this.ticks++;

    this.products = this.products.map(p => {
      const own = [...p.own.slice(1), p.own[p.own.length - 1] + randomBetween(-0.05, 0.055)];
      const zone = [...p.zone.slice(1), p.zone[p.zone.length - 1] + randomBetween(-0.04, 0.045)];
      return { ...p, own, zone };
    });

    if (this.chart) {
      const current = this.product;
      this.pushPoint(this.chart.series[0], current.own[current.own.length - 1], true);
      this.pushPoint(this.chart.series[1], current.zone[current.zone.length - 1], false);
      this.chart.redraw();
    }

    if (this.ticks % PRICE_EVERY === 0) { this.movePrice(); }
    if (this.ticks % PRODUCT_EVERY === 0) { this.nextProduct(); }
  }

  /** Agrega la lectura y traslada el marcador: solo el último punto lo lleva. */
  private pushPoint(series: Highcharts.Series, value: number, marker: boolean): void {
    const previous = series.points[series.points.length - 1];
    previous?.update({ marker: { enabled: false } }, false);
    series.addPoint(marker ? this.lastPoint(value) : value, false, true);
  }

  private lastPoint(value: number): Highcharts.PointOptionsObject {
    return { y: value, marker: { enabled: true, radius: LAST_POINT_MARKER_RADIUS } };
  }

  /**
   * Rota el producto. Repinta las dos series con su color y su historia: es el
   * mismo dato visto en otro combustible, así que la gráfica se transforma en
   * lugar de reconstruirse, y el tablero se reordena con los precios nuevos.
   */
  private nextProduct(): void {
    const index = this.products.findIndex(p => p.key === this.active);
    this.active = this.products[(index + 1) % this.products.length].key;

    const next = this.product;
    const transparent = Highcharts.color(next.color).setOpacity(0.35).get('rgba') as string;

    if (this.chart) {
      this.chart.series[0].update({ type: 'line', color: next.color }, false);
      this.chart.series[1].update({ type: 'line', color: transparent }, false);
      this.chart.series[0].setData(this.withLastMarker(next.own), false);
      this.chart.series[1].setData(next.zone, false);
      this.chart.redraw();
    }
  }

  private withLastMarker(values: number[]): Highcharts.PointOptionsType[] {
    return values.map((v, i) => (i === values.length - 1 ? this.lastPoint(v) : v));
  }

  /**
   * Un solo movimiento por evento: varios a la vez se leen como ruido aleatorio,
   * uno solo se lee como un competidor que acaba de cambiar su precio. Se mueve
   * el producto activo, que es el que se está viendo.
   */
  private movePrice(): void {
    const target = this.rows[Math.floor(Math.random() * this.rows.length)];
    const step = randomBetween(-0.2, 0.2);

    this.rows = this.rows.map(row =>
      row.id === target.id
        ? {
            ...row,
            prices: { ...row.prices, [this.active]: row.prices[this.active] + step },
            fresh: true
          }
        : row
    );

    this.timeouts.push(setTimeout(() => {
      this.rows = this.rows.map(row => (row.id === target.id ? { ...row, fresh: false } : row));
    }, FRESH_MS));
  }

  // ── Opciones de la gráfica ─────────────────────────────────────────────────

  private buildOptions(): Highcharts.Options {
    const current = this.product;
    const transparent = Highcharts.color(current.color).setOpacity(0.35).get('rgba') as string;

    const axisLabelStyle: Highcharts.CSSObject = {
      fontSize: '10px',
      fontWeight: '500',
      color: AXIS_LABEL_COLOR,
      // Cifras de ancho fijo: las etiquetas del eje no bailan al llegar una lectura.
      fontFeatureSettings: '"tnum"'
    };

    return {
      credits: { enabled: false },
      chart: {
        height: 158,
        // Márgenes propios: sin ellos Highcharts reserva sitio de título y leyenda.
        margin: [8, 6, 26, 44],
        animation: { duration: 420 },
        backgroundColor: 'transparent',
        style: { fontFamily: "'Source Sans Pro', 'Open Sans', 'Segoe UI', sans-serif" }
      },
      title: { text: undefined },
      legend: { enabled: false },
      // El botón hamburguesa del SVG se apaga, como en Evolución: la gráfica del
      // login no se exporta.
      exporting: { enabled: false, buttons: { contextButton: { enabled: false } } },
      xAxis: {
        // Sin holgura ni ajuste a marca: el borde derecho es la última lectura.
        minPadding: 0,
        maxPadding: 0,
        startOnTick: false,
        endOnTick: false,
        tickPositioner: function (this: Highcharts.Axis): number[] {
          const max = this.max ?? 0;
          return X_TICK_OFFSETS.map(offset => max - offset);
        } as Highcharts.AxisTickPositionerCallbackFunction,
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
            const max = this.axis.max ?? 0;
            return X_LABELS[Math.round(max - (this.value as number))] || '';
          },
          style: axisLabelStyle,
          y: 16
        },
        // Sin rejilla por lectura: 34 verticales leen como jaula.
        gridLineWidth: 0,
        tickLength: 0,
        lineWidth: 0,
        crosshair: { width: 1, color: AXIS_GRID_COLOR, dashStyle: 'Dash' }
      },
      yAxis: {
        title: { text: null },
        tickAmount: Y_AXIS_CUTS,
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
            return `$${(this.value as number).toFixed(2)}`;
          },
          style: axisLabelStyle,
          align: 'right',
          x: -6
        },
        gridLineDashStyle: 'Dash',
        gridLineColor: AXIS_GRID_COLOR,
        lineWidth: 0,
        // Holgura relativa en vez de mínimo fijo: los tres productos viven en
        // rangos distintos y un mínimo duro aplastaría la línea de los caros.
        minPadding: Y_PADDING,
        maxPadding: Y_PADDING
      },
      tooltip: {
        shared: true,
        outside: true,
        backgroundColor: TOOLTIP_BG,
        borderColor: 'rgba(255, 255, 255, .16)',
        style: { color: '#ffffff' },
        formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
          return (this.points || [])
            .map(p => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${money(p.y as number)}</b>`)
            .join('<br/>');
        }
      },
      plotOptions: {
        series: {
          animation: { duration: 420 },
          marker: { enabled: false, radius: 2 },
          lineWidth: 2,
          states: { inactive: { opacity: 1 } }
        }
      },
      series: [
        {
          type: 'line',
          name: 'Tu estación',
          data: this.withLastMarker(current.own),
          color: current.color
        },
        {
          // La referencia va punteada y en el mismo color al 35%, igual que la
          // serie de comparación de Evolución.
          type: 'line',
          name: 'Promedio de zona',
          data: current.zone,
          color: transparent,
          dashStyle: 'ShortDash'
        }
      ]
    };
  }
}
